import {
    clearAnonymousProject,
    writeAnonymousProject,
    type AnonymousMigration,
    type AnonymousProjectDraft,
} from "./anonymous-project-store";
import {isProjectLimitReached} from "./project-list-actions";
import {responseProjectEtag} from "./project-etag";

interface CreatedProject {
    project: { id: string };
}

interface MigrationDependencies {
    clearDraft?: typeof clearAnonymousProject;
    fetch?: typeof globalThis.fetch;
    writeDraft?: typeof writeAnonymousProject;
}

export const migrateAnonymousDraft = async (
    draft: AnonymousProjectDraft,
    csrfToken: string,
    dependencies: MigrationDependencies = {},
): Promise<string> => {
    const fetchImpl = dependencies.fetch ?? globalThis.fetch;
    const writeDraft = dependencies.writeDraft ?? writeAnonymousProject;
    const clearDraft = dependencies.clearDraft ?? clearAnonymousProject;
    const csrfHeaders = { "X-CSRF-Token": csrfToken };
    let migration: AnonymousMigration | undefined = draft.migration;

    if (migration) {
        const current = await fetchImpl(`/api/projects/${migration.projectId}`);
        if (current.ok) migration = { ...migration, etag: responseProjectEtag(current) };
        else if (current.status === 404) migration = undefined;
        else throw new Error("MIGRATION_LOOKUP_FAILED");
    }

    if (!migration) {
        const created = await fetchImpl("/api/projects", {
            method: "POST",
            headers: { "Content-Type": "application/json", ...csrfHeaders },
            body: JSON.stringify({ name: draft.name }),
        });
        if (await isProjectLimitReached(created)) throw new Error("PROJECT_LIMIT_REACHED");
        if (!created.ok) throw new Error("PROJECT_CREATE_FAILED");
        const body = (await created.json()) as CreatedProject;
        migration = { projectId: body.project.id, etag: responseProjectEtag(created) };
        await writeDraft({ ...draft, migration });
    }

    const contentResponse = await fetchImpl(`/api/projects/${migration.projectId}/content`, {
        method: "PUT",
        headers: {
            "Content-Type": "application/x.scratch.sb3",
            "If-Match": migration.etag,
            ...csrfHeaders,
        },
        body: draft.content,
    });
    if (!contentResponse.ok) throw new Error("PROJECT_CONTENT_FAILED");
    migration = { ...migration, etag: responseProjectEtag(contentResponse) };
    await writeDraft({ ...draft, migration });

    if (draft.thumbnail) {
        const thumbnailResponse = await fetchImpl(`/api/projects/${migration.projectId}/thumbnail`, {
            method: "PUT",
            headers: {
                "Content-Type": "image/png",
                "If-Match": migration.etag,
                ...csrfHeaders,
            },
            body: draft.thumbnail,
        });
        if (!thumbnailResponse.ok) throw new Error("PROJECT_THUMBNAIL_FAILED");
    }

    await clearDraft();
    return migration.projectId;
};
